/** Which pass over a question found a fault. */
export enum QuestionFaultOrigin {
  Digitisation = 'digitisation',
  Enrichment = 'enrichment',
}

/**
 * Faults a returned tree can have that its schema cannot rule out. Each breaks
 * a rule a prompt already sets, so a fault reads back to the model as a
 * correction, not a new instruction.
 */
export enum FaultCode {
  /** A node's marks differ from the sum of its parts' marks. */
  MarksDoNotSum = 'marks_do_not_sum',
  /** A node's criteria award a different total from its marks. */
  CriteriaDoNotSum = 'criteria_do_not_sum',
  /** Some of a node's parts have marks and others do not. */
  MarksPartlyAllocated = 'marks_partly_allocated',
  /** The survey read marks for the question, but the tree gives it none. */
  MarksDisagreeWithSurvey = 'marks_disagree_with_survey',
  /** The surveyed items' marks do not add up to the paper's printed total. */
  TotalMarksDoNotSum = 'total_marks_do_not_sum',
  /** The tree answers fewer parts than the survey counted items under the question. */
  FewerPartsThanSurveyed = 'fewer_parts_than_surveyed',
  /** A body's LaTeX does not render. */
  InvalidLatex = 'invalid_latex',
  /** A leaf with an empty body. */
  EmptyLeafBody = 'empty_leaf_body',
  /** Wording that the pages' legible text layer does not contain. */
  WordingNotOnThePage = 'wording_not_on_the_page',
  /** Criteria, options, option headings or a solution on a node that has parts. */
  ContentOnBranch = 'content_on_branch',
  DuplicatePartLabel = 'duplicate_part_label',
  /** The tokens in a body and its choices do not match its diagrams one to one. */
  DiagramTokenMismatch = 'diagram_token_mismatch',
  /** A token inside maths, which the renderer draws as its own characters. */
  DiagramTokenInMaths = 'diagram_token_in_maths',
  /** Two diagrams share a token, so one crop is dropped. */
  DuplicateDiagramToken = 'duplicate_diagram_token',
  /** A box with no area, which cannot be cropped. */
  DiagramBoxEmpty = 'diagram_box_empty',
  /** A box on a page the extraction was not shown, which cannot be cropped. */
  DiagramPageNotShown = 'diagram_page_not_shown',
  /** A diagram's caption written beside its token as well, where the crop already shows it. */
  DiagramLabelInBody = 'diagram_label_in_body',
  /** A choice's picture captioned with a choice's letter, which stays out of its crop. */
  ChoiceLetterAsCaption = 'choice_letter_as_caption',
  /** More than one option is marked correct. */
  MultipleCorrectOptions = 'multiple_correct_options',
  /** A single option, which means the question was wrongly digitised as multiple-choice. */
  TooFewOptions = 'too_few_options',
  /**
   * A table of choices that is not whole: an option whose cells do not match the
   * node's headings in number, cells with no headings, or headings with no options.
   */
  OptionTableMismatch = 'option_table_mismatch',
  /** An option with both a body and cells, or with neither. */
  OptionBodyAndCells = 'option_body_and_cells',
  /** A body that repeats the option headings, which belong in optionHeadings alone. */
  OptionHeadingsInBody = 'option_headings_in_body',
  /** A stimulus placed before a part the node does not have. */
  StimulusOutOfPlace = 'stimulus_out_of_place',
  /** A dependency on a label that is not another part of the question. */
  UnresolvedDependency = 'unresolved_dependency',
  DuplicateQuestionId = 'duplicate_question_id',
  /** A page range that runs backwards, or off the end of the document. */
  PageRangeUnusable = 'page_range_unusable',
  /** The question carries on past the pages its run was shown. */
  QuestionCutShort = 'question_cut_short',
  /** Parts its paper's solutions paper was searched for an answer to and gave none. */
  SolutionNotFound = 'solution_not_found',
}

/** Why enrichment parked a question in `draft` rather than enriching it. */
export enum EnrichmentParkCode {
  /** The question records no subject. */
  NoSubject = 'no_subject',
  /** No catalogue is loaded for the question's subject. */
  NoCatalogue = 'no_catalogue',
  /** The subject's catalogue offered no candidate topic for the question. */
  NoTopicCandidates = 'no_topic_candidates',
  /** No part of the question could be given a topic. */
  NoTopic = 'no_topic',
}
